import { useCallback, useEffect, useRef, useState } from 'react';

import { Hub, hubsAPI } from '@/services/api/hubs';
import {
  ProviderClient,
  getProviderClients,
} from '@/services/api/providers';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

// ─────────────────────────────────────────────────────────────────────────────
// Hub options — the pitstop list is small (≤100) and returned in one shot, so
// we fetch once when enabled and let <SearchableDropdown> filter locally.
// ─────────────────────────────────────────────────────────────────────────────
export interface UseHubOptionsResult {
  hubs: Hub[];
  loading: boolean;
  error: string | null;
  hubById: (id: string | number) => Hub | undefined;
}

export function useHubOptions(enabled: boolean): UseHubOptionsResult {
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (!enabled || fetchedRef.current) return;
    let cancelled = false;
    fetchedRef.current = true;
    setLoading(true);
    setError(null);
    hubsAPI
      .list()
      .then((res) => {
        if (!cancelled) setHubs(res);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        fetchedRef.current = false; // allow a retry on next enable
        setError(e instanceof Error ? e.message : 'Gagal memuat hub');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const hubById = useCallback(
    (id: string | number) => hubs.find((h) => String(h.id) === String(id)),
    [hubs]
  );

  return { hubs, loading, error, hubById };
}

// ─────────────────────────────────────────────────────────────────────────────
// Provider clients — paginated + name-searchable, scoped to a providerId.
// Mirrors useProvidersDropdown but keyed on the selected provider so switching
// providers resets and refetches from page 1.
// ─────────────────────────────────────────────────────────────────────────────
export interface UseProviderClientsResult {
  options: ProviderClient[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  setSearch: (value: string) => void;
  loadMore: () => void;
  clientById: (id: string | number) => ProviderClient | undefined;
  labelFor: (id: string | number) => string;
}

export function useProviderClientsDropdown(
  providerId: string,
  enabled: boolean
): UseProviderClientsResult {
  const [options, setOptions] = useState<ProviderClient[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const labelCache = useRef<Map<string, string>>(new Map());
  const clientCache = useRef<Map<string, ProviderClient>>(new Map());

  const active = enabled && !!providerId;

  // Reset whenever the provider changes or the picker is disabled.
  useEffect(() => {
    setSearch('');
    setOptions([]);
    setPage(1);
    setHasMore(false);
    setError(null);
  }, [providerId, enabled]);

  // Page-1 fetch (initial + on search change), debounced like the provider hook.
  useEffect(() => {
    if (!active) return;
    const trimmed = search.trim();
    let cancelled = false;

    const fire = () => {
      setLoading(true);
      setError(null);
      getProviderClients(providerId, {
        page: '1',
        size: String(PAGE_SIZE),
        name: trimmed || undefined,
      })
        .then((res) => {
          if (cancelled) return;
          const data = res.data ?? [];
          setOptions(data);
          setPage(1);
          setHasMore((res.pagination?.lastPage ?? 1) > 1);
          for (const c of data) {
            labelCache.current.set(String(c.id), c.name);
            clientCache.current.set(String(c.id), c);
          }
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          setError(e instanceof Error ? e.message : 'Gagal memuat client');
          setOptions([]);
          setHasMore(false);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };

    let timer: ReturnType<typeof setTimeout> | undefined;
    if (trimmed) {
      timer = setTimeout(fire, SEARCH_DEBOUNCE_MS);
    } else {
      fire();
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [active, providerId, search]);

  const loadMore = useCallback(() => {
    if (!active || loading || loadingMore || !hasMore) return;
    const nextPage = page + 1;
    const trimmed = search.trim();
    setLoadingMore(true);
    getProviderClients(providerId, {
      page: String(nextPage),
      size: String(PAGE_SIZE),
      name: trimmed || undefined,
    })
      .then((res) => {
        const data = res.data ?? [];
        setOptions((prev) => [...prev, ...data]);
        setPage(nextPage);
        setHasMore(nextPage < (res.pagination?.lastPage ?? 0));
        for (const c of data) {
          labelCache.current.set(String(c.id), c.name);
          clientCache.current.set(String(c.id), c);
        }
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Gagal memuat client lain');
        setHasMore(false);
      })
      .finally(() => setLoadingMore(false));
  }, [active, loading, loadingMore, hasMore, page, search, providerId]);

  const clientById = useCallback(
    (id: string | number) => clientCache.current.get(String(id)),
    []
  );
  const labelFor = useCallback(
    (id: string | number) => labelCache.current.get(String(id)) ?? '',
    []
  );

  return {
    options,
    loading,
    loadingMore,
    hasMore,
    error,
    setSearch,
    loadMore,
    clientById,
    labelFor,
  };
}
